// frontend/src/pages/AccountingTypes/AccountingType.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Building,
  CheckCircle,
  Layers,
  Loader2,
  Lock,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { useAuth } from "../../contexts/AuthContext";
import "./AccountingType.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const FIXED_TYPES = [
  ["AST-CURRENT", "Asset", "Current Asset", "DR", "Balance Sheet"],
  ["LIA-CURRENT", "Liability", "Current Liability", "CR", "Balance Sheet"],
  ["CAP-EQUITY", "Equity", "Equity", "CR", "Balance Sheet"],
  ["REV-INCOME", "Income", "Income", "CR", "Income Statement"],
  ["REV-CONTRA", "Income", "Contra Income", "DR", "Income Statement"],
  ["EXP-COS", "Expense", "Cost of Sales", "DR", "Income Statement"],
  [
    "EXP-COS-CONTRA",
    "Expense",
    "Contra Cost of Sales",
    "CR",
    "Income Statement",
  ],
  ["EXP-OPER", "Expense", "Operating Expense", "DR", "Income Statement"],
];

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id || value.id || "";
  return value;
};

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (
      typeof company.currency === "string" &&
      /^[A-Z]{3}$/.test(company.currency)
    ) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (
      typeof company.currency_code === "string" &&
      /^[A-Z]{3}$/.test(company.currency_code)
    ) {
      currency = company.currency_code;
    } else if (
      typeof company.currency_code === "object" &&
      company.currency_code.code
    ) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (
      typeof company.base_currency === "string" &&
      /^[A-Z]{3}$/.test(company.base_currency)
    ) {
      currency = company.base_currency;
    } else if (
      typeof company.base_currency === "object" &&
      company.base_currency.code
    ) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const AccountingType = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();

  const isSystemAdmin =
    userType === "system" &&
    ["developer", "admin"].includes(currentUser?.role);

  // ✅ Company currency derived safely (used to force the setup to KES/etc.)
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(
    userType === "company"
      ? getId(company) || getId(currentUser?.company_id)
      : "",
  );
  const [loading, setLoading] = useState(isSystemAdmin);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isSystemAdmin) return;

    const loadCompanies = async () => {
      try {
        const response = await axios.get(`${url}/api/company`, {
          headers: getAuthHeaders(),
        });
        setCompanies(response.data?.data || []);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to load companies",
        );
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [isSystemAdmin]);

  const selectedCompanyName = useMemo(() => {
    if (!isSystemAdmin) return company?.company_name || "your company";
    return (
      companies.find((item) => String(item._id) === String(companyId))
        ?.company_name || "the selected company"
    );
  }, [companies, company, companyId, isSystemAdmin]);

  // ✅ Resolve the currency to force for the selected company.
  // - Company user -> their own company's currency
  // - System admin -> currency of the company they picked in the dropdown
  const targetCurrency = useMemo(() => {
    if (!isSystemAdmin) return companyCurrency;

    const pickedCompany = companies.find(
      (item) => String(item._id) === String(companyId),
    );
    return getCompanyCurrency(pickedCompany);
  }, [isSystemAdmin, companyCurrency, companies, companyId]);

  const installSetup = async () => {
    if (!companyId) {
      toast.error("Select a company first");
      return;
    }

    const confirmed = window.confirm(
      `Install the fixed inventory accounting setup for ${selectedCompanyName}?\n\n` +
      `The eight types and the protected accounts will be created or repaired. ` +
      `Unused legacy starter accounts may be archived, and every protected ` +
      `account (plus its existing subaccounts) will be set to ${targetCurrency}.`,
    );

    if (!confirmed) return;

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${url}/api/account/setup-inventory`,
        {
          company_id: companyId,
          cleanup_legacy: true,
          force_currency_update: true,   // ⭐ align protected accounts to company currency
          currency_code: targetCurrency, // ⭐ explicit code (KES in your case)
        },
        { headers: getAuthHeaders() },
      );

      toast.success(
        response.data?.message || "Inventory accounting setup installed",
      );
      navigate("/ViewAccountingTypes");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to install inventory accounting setup",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="accounting-loading-screen">
        <Loader2 className="animate-spin" size={38} />
        <p>Loading accounting setup…</p>
      </div>
    );
  }

  return (
    <div className="accounting-type-page">
      <div className="accounting-page-shell">
        <header className="accounting-page-header">
          <div>
            <span className="accounting-page-kicker">Inventory accounting</span>
            <h1>
              <Layers size={27} /> Fixed Accounting Types
            </h1>
            <p>
              This Inventory Management System uses only eight protected
              accounting classifications. Users add chart subaccounts instead
              of creating new accounting types.
            </p>
          </div>
          <div className="accounting-header-badge">
            <Lock size={18} /> System managed
          </div>
        </header>

        {isSystemAdmin && (
          <section className="accounting-company-panel">
            <div className="company-panel-icon">
              <Building size={21} />
            </div>
            <div className="accounting-field full">
              <label>Company *</label>
              <select
                value={companyId}
                onChange={(event) => setCompanyId(event.target.value)}
              >
                <option value="">Select company</option>
                {companies.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.company_name}
                    {item.company_code ? ` (${item.company_code})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </section>
        )}

        <section className="accounting-quick-types">
          <div className="section-heading">
            <Lock size={18} />
            <div>
              <h2>Protected Inventory Classifications</h2>
              <p>Normal balance and financial statement are automatic.</p>
            </div>
          </div>

          <div className="account-type-grid">
            {FIXED_TYPES.map(([code, type, subType, normal, statement]) => (
              <div className="account-type-option" key={code}>
                <div>
                  <strong>{subType}</strong>
                  <span>
                    {code} · {type} · {statement}
                  </span>
                </div>
                <b className={`balance-sign ${normal.toLowerCase()}`}>
                  {normal}
                </b>
              </div>
            ))}
          </div>
        </section>

        <section className="standard-setup-panel">
          <div>
            <span className="panel-eyebrow">One controlled setup</span>
            <h2>Install or repair the inventory ledger</h2>
            <p>
              Creates the eight fixed types and the protected accounts. It is
              safe to run again because matching records are repaired rather than
              duplicated. Protected accounts and their existing subaccounts are
              aligned to the company currency ({targetCurrency}).
            </p>
          </div>

          <button
            type="button"
            className="create-standard-btn"
            onClick={installSetup}
            disabled={submitting || !companyId}
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" size={18} /> Installing…
              </>
            ) : (
              <>
                <RefreshCw size={18} /> Install / Repair Setup
              </>
            )}
          </button>
        </section>

        <div className="accounting-rule-note">
          <CheckCircle size={19} />
          <p>
            The protected types and accounts cannot be edited, disabled or
            deleted. Manual accounts must be created as automatically numbered
            subaccounts in the Chart of Accounts.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountingType;